<template>
  <div class="audio-response-view">
    <cv-form>
      <question-columns-layout :align-two-columns="alignTwoColumns">
        <template #left>
          <!-- Instructions -->
          <dl class="field-set type-text instructions">
            <dt class="field-label">
              <svg-icon name="Info16" kind="indicative" />
              <span>{{ t("question_component_label_instructions") }}</span>
            </dt>
            <dd v-safe-html="instructionContent" class="field-value"></dd>
          </dl>

          <!-- Question Body -->
          <match-indicator v-if="highlights" />
          <dl class="field-set type-text question-body">
            <dd class="field-value">
              <rich-text-wrapper
                :content="questionBody"
                :expandable="true"
                :expanded="true"
                :can-annotate="canAccessAnnotations"
                :anchor="questionBodyAnchor"
                :resource-type="'LESSON_COMPONENT'"
                :annotation-resource-id="questionAnnotationResourceId"
              />
            </dd>
          </dl>
        </template>
        <template #right>
          <!-- Integrator Request Banner -->
          <integrator-request-banner
            v-if="isSpeechEvaluationQuestion"
            :app-list="appList"
            :option-selected="optionSelected"
          />

          <!-- Answer -->
          <dl class="field-set type-textarea answer">
            <dd class="field-value">
              <cv-text-area
                :disabled="true"
                :placeholder="t('question_component_placeholder_answer_audio_response')"
              />
            </dd>
          </dl>
        </template>
        <template #bottom>
          <!-- Grading rubric -->
          <dl class="field-set grading-rubrics">
            <slot name="grading-rubric" />
          </dl>

          <cv-inline-notification
            v-if="hasSubmitValidationErrors"
            class="notification"
            :class="errorClass"
            :title="submitValidationErrors"
            low-contrast
            kind="error"
          />
          <!-- Suggested Answer, Teacher's Feedback -->
          <dl class="field-set type-text answer-info">
            <dd class="field-value">
              <div class="answer-info-header">
                <cv-content-switcher :id="contentSwitcherId" @selected="handleSectionSelected">
                  <content-switcher-button
                    v-for="(header, $index) in sectionHeaders"
                    :key="key($index)"
                    :parent-switcher="contentSwitcherId"
                    :owner-id="key($index)"
                    :selected="isSectionSelected(key($index))"
                  >
                    {{ header }}
                  </content-switcher-button>
                </cv-content-switcher>
              </div>

              <div class="answer-info-content">
                <cv-content-switcher-content
                  v-for="(section, $index) in sectionDetails"
                  :key="key($index)"
                  :parent-switcher="contentSwitcherId"
                  :owner-id="key($index)"
                >
                  <!-- First tab: Suggested Answer -->
                  <rich-text-card
                    v-if="section.header === t('question_component_suggested_answer_label')"
                    :title="section.header"
                    :content="section.value"
                    :expandable="true"
                    :visible="isSectionSelected(key($index))"
                    truncation-type="secondary"
                    :anchor="suggestedAnswerAnchor"
                    :resource-type="'LESSON_COMPONENT'"
                    :annotation-resource-id="questionAnnotationResourceId"
                    :can-annotate="canAccessAnnotations"
                  >
                    <template #icon>
                      <svg-icon name="BubbleCheck32" kind="indicative" />
                    </template>
                  </rich-text-card>
                  <!-- Second tab: Teacher Feedback -->
                  <rich-text-card v-else :title="section.header" :content="section.value">
                    <template #icon>
                      <svg-icon name="UserFeedback32" kind="indicative" />
                    </template>
                  </rich-text-card>
                </cv-content-switcher-content>
              </div>
            </dd>
          </dl>
        </template>
      </question-columns-layout>
    </cv-form>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import {
  buildQuestionAnchor,
  buildSuggestedAnswerAnchor,
} from "@modules/annotation/service/annotation.anchor.utils.ts";
import IntegratorRequestBanner from "@modules/editor/components/IntegratorRequestBanner.vue";
import { INTEGRATOR_APP_FEATURE_CATEGORY } from "@modules/app/service/app.constant";
import { LFA_CHECKBOX_TYPES, NAMESPACE } from "@modules/attempt/service/attempt.constant";
import { isEmpty } from "@utils/var.check";
import { useSuggestedAnswerSectionSelection } from "@/modules/editor/composables/suggested-answer-section-selection";
import { useQuestionView } from "@modules/editor/composables/question-view.ts";
import { useQuestionAnnotation } from "@modules/editor/composables/use-question-annotation.ts";
import { useAnnotationPermissions } from "@modules/annotation/composables/use-annotation-permissions.ts";
import { Question } from "@/types/question.ts";
import { ResourceContext } from "@plugins/richtext/types.ts";
import { useLessonSubmissionValidation } from "@modules/editor/composables/lesson-submission-validation.ts";
import QuestionColumnsLayout from "@/components/QuestionColumnsLayout.vue";
import { type QuestionLayout } from "@/types/question";
import RichTextWrapper from "@/plugins/richtext/components/RichTextWrapper.vue";
import {
  MAX_DEFAULT_AUDIO_DURATION_MINUTE,
  MAX_SE_AUDIO_DURATION_MINUTE,
} from "@plugins/media/media.constant.ts";

interface Props {
  question: Question;
  gradingRubricUuid?: string;
  isNew?: boolean;
  mode: string;
  editingRubric?: boolean;
  hasActiveSubQuestion?: boolean;
  speechEvaluationLanguage?: string;
  highlights?: object | null;
}

const props = withDefaults(defineProps<Props>(), {
  gradingRubricUuid: "",
  isNew: false,
  editingRubric: false,
  hasActiveSubQuestion: false,
  speechEvaluationLanguage: "",
  highlights: null,
});

const emit = defineEmits<{
  loaded: [questionUuid: string];
}>();

const { t } = useI18n();
const { canAccessAnnotations } = useAnnotationPermissions();
const store = useStore();
const { checkIfUnapprovedMOValidationErrorsExist } = useLessonSubmissionValidation();

const resourceContext: ResourceContext = inject("resourceContext");
const {
  questionAnchor: providedQuestionAnchor,
  questionBodyAnchor,
  questionAnnotationResourceId,
} = useQuestionAnnotation();

// State
const sectionSelected = ref(`view_${props.question.uuid}_0_${props.mode.toLowerCase()}`);
const submitValidationErrors = ref("");
const errorClass = ref("");
const hasSubmitValidationErrors = ref(false);

const sectionHeaders = computed(() => [
  t("question_component_suggested_answer_label"),
  t("question_component_teacher_feedback_label"),
]);

// Computed
const getIntegratorAppAccessByCategory = computed(
  () => store.getters[`${NAMESPACE.APP}/getIntegratorAppAccessByCategory`],
);

const isSpeechEvaluationQuestion = computed(() => {
  return !isEmpty(props.question.speechEvaluation);
});

const getAudioInstructionContent = computed(() => {
  const maxDurationMinutes =
    props.question.maxAudioDurationInSeconds != null
      ? props.question.maxAudioDurationInSeconds / 60
      : isSpeechEvaluationQuestion.value
        ? MAX_SE_AUDIO_DURATION_MINUTE
        : MAX_DEFAULT_AUDIO_DURATION_MINUTE;

  return isSpeechEvaluationQuestion.value
    ? t("se_question_audio_instructions_content", { duration: maxDurationMinutes })
    : t("question_audio_instructions_content", { duration: maxDurationMinutes });
});

const instructionContent = computed(() => {
  if (isTeamResponseQuestion.value) {
    return t("team_response_arq_instructions_content", {
      audioInstruction: getAudioInstructionContent.value,
    });
  }
  return getAudioInstructionContent.value;
});

const questionBody = computed(() => {
  return props.question.questionBody;
});
const sectionDetails = computed(() => [
  {
    header: t("question_component_suggested_answer_label"),
    value: props.question.answerValue
      ? props.question.answerValue
      : t("question_component_label_view_suggested_answer_placeholder"),
  },
  {
    header: t("question_component_teacher_feedback_label"),
    value: t("question_component_label_teacher_feedback_placeholder"),
  },
]);

const isSectionSelected = computed(() => {
  return (section: string) => {
    return section === sectionSelected.value;
  };
});

const key = computed(() => {
  return (value: number) => {
    // questionUuid_index to ensure content switcher has unique key when activity contains multiple question.
    return `view_${props.question.uuid}_${value}_${props.mode.toLowerCase()}`;
  };
});

// IntegratorRequestBanner
const appList = computed(() => {
  const apps = getIntegratorAppAccessByCategory.value(INTEGRATOR_APP_FEATURE_CATEGORY.SE);
  const language = isEmpty(props.speechEvaluationLanguage)
    ? props.question.speechEvaluation
    : props.speechEvaluationLanguage;
  const seApp = apps.find((app) => app.language === language);
  return [seApp];
});

const optionSelected = computed(() => {
  const options = [LFA_CHECKBOX_TYPES.MARKS];
  if (isSpeechEvaluationQuestion.value) return options;
  return [];
});

const contentSwitcherId = computed(() => {
  return `content_switcher_audio_response_view_${props.question.uuid}`;
});

const suggestedAnswerSectionKey = computed(() => {
  return key.value(0);
});

const questionAnchor = computed(() => {
  if (!isEmpty(providedQuestionAnchor.value)) {
    return providedQuestionAnchor.value;
  }

  return isEmpty(props.question.id)
    ? ""
    : buildQuestionAnchor(props.question.id as string | number);
});

const suggestedAnswerAnchor = computed(() => {
  return isEmpty(questionAnchor.value) ? "" : buildSuggestedAnswerAnchor(questionAnchor.value);
});

useSuggestedAnswerSectionSelection({
  sectionSelected,
  suggestedAnswerAnchor,
  suggestedAnswerSectionKey,
});

const { isTeamResponseQuestion, alignTwoColumns } = useQuestionView({
  question: props.question as { parentQuestionUuid?: string; layout?: QuestionLayout },
  resourceContext: resourceContext,
});

// Methods
const checkIfSubmitValidationErrorsExist = () => {
  hasSubmitValidationErrors.value = false;
  if (checkIfUnapprovedMOValidationErrorsExist(props.question.uuid)) {
    hasSubmitValidationErrors.value = true;
    submitValidationErrors.value = t("validation_among_tabs_label");
    errorClass.value = "submit-validation-error";
  }
};

const handleSectionSelected = (selected: string) => {
  sectionSelected.value = selected;
};

onMounted(() => {
  emit("loaded", props.question.uuid);
  checkIfSubmitValidationErrorsExist();
});
</script>

<style lang="scss">
@import "@styles/abstracts/variables";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.audio-response-view {
  .field-set {
    &.answer {
      margin-bottom: $spacing-04;
      .bx--text-area {
        min-height: 7.5rem;
        padding: $spacing-03 $spacing-05;
        background-color: $ui-01;
        border: 1px solid $ui-03;
      }
    }

    &.answer-info {
      margin-top: $spacing-04;
      padding-top: $spacing-02;
      border-top: $divider-02;

      .rich-text-card {
        border: 1px solid $ui-03;

        .header {
          > .icon {
            margin-right: $spacing-03;
          }
        }
      }
    }
  }

  .grading-rubric.empty {
    display: none;
  }
}
</style>
